import { useEffect, useState, useContext, useRef } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import MailIcon from '@mui/icons-material/Mail';
import InfoIcon from '@mui/icons-material/Info';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import SettingsIcon from '@mui/icons-material/Settings';
import { useParams } from 'react-router-dom';
import { IKUpload } from 'imagekitio-react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstChar } from '../../helper/Helper';
import { UserContext } from '../../context/UserContext';
import PageContainer from '../../components/page-container/PageContainer';
import ConfirmDialog from '../../components/confirm-dialog/ConfirmDialog';
import TabPanel, { a11yProps } from '../../components/tab-panel/TabPanel';
import DataTable from '../../components/data-table/DataTable';
import StatusBadge from '../../components/status-badge/StatusBadge';
import DocumentViewer from '../../components/document-viewer/DocumentViewer';

import ProjectApi from '../../services/ProjectApi';
import InvitationApi from '../../services/InvitationApi';
import DocumentApi from '../../services/DocumentApi';
import ImageKitApi from '../../services/ImageKitApi';
import { sendInviteEmail } from '../../services/EmailJSApi';

import PdfToImageUpload from '../../components/pdf-to-image-upload/PdfToImageUpload';
import WatermarkConfigDialog from '../../components/watermark-config-dialog/WatermarkConfigDialog';
import { StatCardSkeleton } from '../../components/skeleton';

const StatCard = ({ icon, label, value, color = 'primary.main' }) => (
  <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h4" fontWeight="bold">{value}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
);

export default function ProjectDetailsPage() {
  const { t } = useTranslation();
  const uploadRef = useRef(null);
  const watermarkUploadRef = useRef(null);
  const projectApi = new ProjectApi();
  const invitationApi = new InvitationApi();
  const documentApi = new DocumentApi();
  const imageKitApi = new ImageKitApi();
  const { user } = useContext(UserContext);
  const { projectId } = useParams();

  const [tabValue, setTabValue] = useState(0);
  const [project, setProject] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentStats, setDocumentStats] = useState({ total: 0, unpaid: 0, paid: 0, downloaded: 0, totalViews: 0 });
  const [participants, setParticipants] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [documentFile, setDocumentFile] = useState(null);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('consumer');

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedViewDocument, setSelectedViewDocument] = useState(null);
  const [viewerMode, setViewerMode] = useState('auto'); // 'auto', 'original', 'watermarked'

  // Watermark config dialog state
  const [watermarkConfigOpen, setWatermarkConfigOpen] = useState(false);
  const [watermarkConfigDoc, setWatermarkConfigDoc] = useState(null);

  // Delete/Restore state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('soft');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Trash section state
  const [showTrash, setShowTrash] = useState(false);
  const [trashedDocuments, setTrashedDocuments] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);

  // Watermark generation state
  const [watermarkingDocId, setWatermarkingDocId] = useState(null);
  const [watermarkSnackbarOpen, setWatermarkSnackbarOpen] = useState(false);
  const watermarkingDocRef = useRef(null);

  // View history state
  const [viewHistoryOpen, setViewHistoryOpen] = useState(false);
  const [viewHistoryDoc, setViewHistoryDoc] = useState(null);
  const [viewHistory, setViewHistory] = useState([]);
  const [loadingViewHistory, setLoadingViewHistory] = useState(false);

  const isProducer = user?.role === 'producer';

  useEffect(() => {
    if (projectId) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const getData = async () => {
    const response = await projectApi.getProject(projectId);
    if (!response.success) {
      console.error('Error fetching project:', response.error);
      setLoading(false);
      return;
    }
    setProject(response.project);
    setName(response.project.name);
    setDescription(response.project.description);

    const response1 = await projectApi.getProjectParticipants(projectId);
    if (response1.success) {
      setParticipants(response1.participants);
    }

    const response2 = await invitationApi.getInvitationByProjectId(projectId);
    if (response2.success) {
      setInvitations(response2.data);
    }

    const response3 = await documentApi.getDocumentsByProjectId(projectId);
    if (response3.success) {
      setDocuments(response3.data);
    }

    const response4 = await documentApi.getDocumentStats(projectId);
    if (response4.success) {
      setDocumentStats(response4.stats);
    }

    setLoading(false);
  };

  const loadTrashedDocuments = async () => {
    setLoadingTrash(true);
    const response = await documentApi.getDeletedDocumentsByProjectId(projectId);
    if (response.success) {
      const trashed = response.data.filter(doc => !doc.isPermanentlyDeleted);
      setTrashedDocuments(trashed);
    } else {
      setSnackbar({ open: true, message: t('documents.trash_load_error'), severity: 'error' });
    }
    setLoadingTrash(false);
  };

  const handleToggleTrash = () => {
    if (!showTrash) {
      loadTrashedDocuments();
    }
    setShowTrash(!showTrash);
  };

  const handleUpdateProject = async () => {
    if (!projectId) return;
    try {
      const response = await projectApi.updateProject(projectId, {
        name,
        description,
        updatedBy: user.uid,
      });
      if (!response.success) {
        setSnackbar({ open: true, message: 'Failed to update project.', severity: 'error' });
        return;
      }

      setSnackbar({ open: true, message: 'Project updated successfully.', severity: 'success' });
      setEditing(false);
      setProject((prev) => ({ ...prev, name, description }));
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to update project.', severity: 'error' });
    }
  };

  const handleAddDocument = async () => {
    triggerIKUpload(documentFile);
  };

  const handleInvite = async () => {
    try {
      const response = await invitationApi.createInvitation({
        email: inviteEmail,
        invitedById: user.uid,
        invitedBy: user.user || null,
        companyId: user.companyId,
        company: user.company || null,
        role: inviteRole,
        projectId: projectId,
      });
      if (!response.success) {
        setSnackbar({ open: true, message: 'Failed to invite user.', severity: 'error' });
        return;
      }
      sendInviteEmail({
        toEmail: inviteEmail,
        inviteLink: `https://www.docuvault.co.za/invite-signup?token=${response?.token}`,
        inviteCompany: user?.company?.name || null,
        inviteBy: user?.user?.name || null,
        inviteRole: inviteRole?.charAt(0)?.toUpperCase() + inviteRole?.slice(1),
      });
      setSnackbar({ open: true, message: 'User invited successfully.', severity: 'success' });
      setInviteDialogOpen(false);
      setInviteEmail('');
      getData();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to invite user.', severity: 'error' });
    }
  };

  const deleteInvitation = async (token) => {
    try {
      const response = await invitationApi.deleteInvitation(token);
      if (!response.success) {
        setSnackbar({ open: true, message: 'Failed to delete invitation.', severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: 'Invitation deleted successfully.', severity: 'success' });
      getData();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to delete invitation.', severity: 'error' });
    }
  };

  const triggerIKUpload = (file) => {
    setUploading(true);
    if (!uploadRef.current) return;
    const input = uploadRef.current.querySelector('input[type="file"]');
    if (!input) return;
    const dataTransfer = new DataTransfer();

    // Determine file extension from original file type
    const extension = file.type === 'application/pdf' ? '.pdf' :
                      file.type.startsWith('image/') ? '.' + file.type.split('/')[1] : '';
    const fileName = `${documentTitle}${extension}`;

    // Upload the original file (not watermarked)
    const fileForUpload = new File([file], fileName, { type: file.type });

    dataTransfer.items.add(fileForUpload);
    input.files = dataTransfer.files;
    const event = new Event('change', { bubbles: true });
    input.dispatchEvent(event);
  };

  const handleUploadSuccess = async (res) => {
    await documentApi.createDocument({
      title: documentTitle,
      description: documentDescription || null,
      url: res.url,
      filePath: res.filePath,
      fileId: res.fileId,
      fileType: documentFile?.type || null,
      fileName: documentFile?.name || null,
      ownerId: user.uid,
      owner: user.user || null,
      companyId: user.companyId || null,
      company: user.company || null,
      projectId: projectId,
    });
    getData();

    setDocumentFile(null);
    setDocumentDialogOpen(false);
    setDocumentTitle('');
    setDocumentDescription('');
    setUploading(false);
    setSnackbar({ open: true, message: t('documents.upload_success'), severity: 'success' });
  };

  const handleViewDocument = (doc, mode = 'auto') => {
    setSelectedViewDocument(doc);
    setViewerMode(mode);
    setViewerOpen(true);
  };

  const openWatermarkConfig = (doc) => {
    setWatermarkConfigDoc(doc);
    setWatermarkConfigOpen(true);
  };

  const handleWatermarkConfigGenerate = async (blob) => {
    if (!watermarkConfigDoc) return;

    setWatermarkConfigOpen(false);
    setWatermarkingDocId(watermarkConfigDoc.id);
    watermarkingDocRef.current = watermarkConfigDoc;
    setWatermarkSnackbarOpen(true);

    // Trigger the hidden IKUpload
    if (watermarkUploadRef.current) {
      const input = watermarkUploadRef.current.querySelector('input[type="file"]');
      if (input) {
        const dataTransfer = new DataTransfer();
        const fileName = `${watermarkConfigDoc.title}_watermarked.pdf`;
        const file = new File([blob], fileName, { type: 'application/pdf' });
        dataTransfer.items.add(file);
        input.files = dataTransfer.files;
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);
      }
    }

    setWatermarkConfigDoc(null);
  };

  const handleStatusChange = async (docId, newStatus) => {
    const response = await documentApi.updateDocumentStatus(docId, newStatus, user.uid);
    if (response.success) {
      setSnackbar({ open: true, message: t('documents.status_updated'), severity: 'success' });
      getData();
    } else {
      setSnackbar({ open: true, message: response.error, severity: 'error' });
    }
  };

  const handleOpenViewHistory = async (doc) => {
    setViewHistoryDoc(doc);
    setViewHistoryOpen(true);
    setLoadingViewHistory(true);

    const response = await documentApi.getDocumentViewHistory(doc.id);
    if (response.success) {
      setViewHistory(response.history);
    } else {
      setSnackbar({ open: true, message: response.error, severity: 'error' });
    }
    setLoadingViewHistory(false);
  };

  const handleCloseViewHistory = () => {
    setViewHistoryOpen(false);
    setViewHistoryDoc(null);
    setViewHistory([]);
  };

  // Delete handlers
  const handleDeleteClick = (doc, type) => {
    setSelectedDocument(doc);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocument) return;

    setDeleting(true);
    try {
      let response;
      if (deleteType === 'soft') {
        response = await documentApi.softDeleteDocument(selectedDocument.id);
      } else {
        response = await documentApi.hardDeleteDocument(selectedDocument.id);
      }

      if (response.success) {
        setSnackbar({
          open: true,
          message: deleteType === 'soft' ? t('documents.soft_delete_success') : t('documents.hard_delete_success'),
          severity: 'success'
        });
        getData();
        if (showTrash) {
          loadTrashedDocuments();
        }
      } else {
        setSnackbar({ open: true, message: response.error, severity: 'error' });
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setSnackbar({ open: true, message: t('documents.delete_error'), severity: 'error' });
    }

    setDeleting(false);
    setDeleteDialogOpen(false);
    setSelectedDocument(null);
  };

  // Restore handlers
  const handleRestoreClick = (doc) => {
    setSelectedDocument(doc);
    setRestoreDialogOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!selectedDocument) return;

    setRestoring(true);
    try {
      const response = await documentApi.restoreDocument(selectedDocument.id);
      if (response.success) {
        setSnackbar({ open: true, message: t('documents.restore_success'), severity: 'success' });
        getData();
        loadTrashedDocuments();
      } else {
        setSnackbar({ open: true, message: response.error, severity: 'error' });
      }
    } catch (error) {
      console.error('Error restoring document:', error);
      setSnackbar({ open: true, message: t('documents.restore_error'), severity: 'error' });
    }

    setRestoring(false);
    setRestoreDialogOpen(false);
    setSelectedDocument(null);
  };

  // Watermark upload handlers
  const handleWatermarkUploadSuccess = async (res) => {
    const doc = watermarkingDocRef.current;
    if (!doc) return;

    try {
      // Delete old watermarked file from ImageKit if it exists
      if (doc.watermarkedFileId) {
        const deleteResult = await imageKitApi.deleteFile(doc.watermarkedFileId);
        if (!deleteResult.success) {
          console.warn('Failed to delete old watermarked file:', deleteResult.error);
          // Continue anyway - old file orphaned but new one will be set
        }
      }

      // Update Firestore with new watermarked version
      const response = await documentApi.setWatermarkedVersion(doc.id, {
        url: res.url,
        filePath: res.filePath,
        fileId: res.fileId,
      });

      setWatermarkSnackbarOpen(false);

      if (response.success) {
        setSnackbar({ open: true, message: t('documents.watermark_success'), severity: 'success' });
        getData();
      } else {
        setSnackbar({ open: true, message: response.error, severity: 'error' });
      }
    } catch (error) {
      console.error('Error saving watermarked version:', error);
      setWatermarkSnackbarOpen(false);
      setSnackbar({ open: true, message: t('documents.watermark_error'), severity: 'error' });
    }

    setWatermarkingDocId(null);
    watermarkingDocRef.current = null;
  };

  const handleWatermarkUploadError = (error) => {
    console.error('Watermark upload error:', error);
    setWatermarkSnackbarOpen(false);
    setSnackbar({ open: true, message: t('documents.watermark_upload_error'), severity: 'error' });
    setWatermarkingDocId(null);
    watermarkingDocRef.current = null;
  };

  // Helper to normalize legacy status values for display
  const normalizeStatus = (status) => {
    if (status === 'uploaded' || status === 'viewed') return 'unpaid';
    if (status === 'downloaded') return 'paid';
    return status || 'unpaid';
  };

  // Document table columns
  const documentColumns = [
    {
      field: 'title',
      headerName: t('documents.name'),
      searchable: true,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: t('documents.status'),
      minWidth: 120,
      renderCell: (row) => <StatusBadge status={row.status || 'unpaid'} />,
    },
    // View count column - only shown for producers
    ...(isProducer ? [{
      field: 'viewCount',
      headerName: t('documents.views'),
      minWidth: 80,
      renderCell: (row) => (
        <Tooltip title={t('documents.view_history')}>
          <Button
            size="small"
            variant="text"
            onClick={(e) => { e.stopPropagation(); handleOpenViewHistory(row); }}
            sx={{ minWidth: 'auto' }}
          >
            {row.viewCount || 0}
          </Button>
        </Tooltip>
      ),
    }] : []),
    {
      field: 'owner',
      headerName: t('documents.uploaded_by'),
      minWidth: 150,
      renderCell: (row) => row.owner?.email || 'Unknown',
    },
    {
      field: 'createdAt',
      headerName: t('documents.date'),
      minWidth: 120,
      renderCell: (row) => row.createdAt?.toDate?.()?.toLocaleDateString() || '-',
    },
    {
      field: 'actions',
      headerName: t('documents.actions'),
      sortable: false,
      minWidth: 280,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isProducer ? (
            <>
              {/* View Original */}
              <Tooltip title={t('documents.view_original')}>
                <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleViewDocument(row, 'original'); }}>
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* View Watermarked (if exists) */}
              {row.watermarkedUrl && (
                <Tooltip title={t('documents.view_watermarked')}>
                  <IconButton size="small" color="secondary" onClick={(e) => { e.stopPropagation(); handleViewDocument(row, 'watermarked'); }}>
                    <BrandingWatermarkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Watermark config button */}
              {watermarkingDocId === row.id ? (
                <CircularProgress size={20} sx={{ mx: 1 }} />
              ) : (
                <Tooltip title={row.watermarkedUrl ? t('watermarkConfig.regenerate') : t('watermarkConfig.generate')}>
                  <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); openWatermarkConfig(row); }}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={normalizeStatus(row.status)}
                  onChange={(e) => { e.stopPropagation(); handleStatusChange(row.id, e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                >
                  <MenuItem value="unpaid">{t('status.unpaid')}</MenuItem>
                  <MenuItem value="paid">{t('status.paid')}</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={t('documents.move_to_trash')}>
                <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleDeleteClick(row, 'soft'); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title={t('documents.view')}>
              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleViewDocument(row); }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <Paper elevation={3} sx={{ mt: 4, p: 4, borderRadius: 2 }}>
          {/* Header skeleton */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Skeleton variant="text" width={300} height={48} />
              <Skeleton variant="text" width={400} height={24} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton variant="rounded" width={100} height={36} />
              <Skeleton variant="rounded" width={120} height={36} />
            </Box>
          </Box>

          {/* Tabs skeleton */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="rounded" width={100} height={48} />
              <Skeleton variant="rounded" width={100} height={48} />
              <Skeleton variant="rounded" width={80} height={48} />
              <Skeleton variant="rounded" width={100} height={48} />
            </Box>
          </Box>

          {/* Stats skeleton */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[...Array(4)].map((_, index) => (
              <Grid size={{ xs: 6, sm: 3 }} key={index}>
                <StatCardSkeleton height={100} />
              </Grid>
            ))}
          </Grid>

          {/* Content skeleton */}
          <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={100} />
        </Paper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Paper elevation={3} sx={{ mt: 4, p: 4, borderRadius: 2 }}>
        {/* Header */}
        {isProducer && editing ? (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={handleUpdateProject}>Save Changes</Button>
            <Button sx={{ ml: 2 }} onClick={() => setEditing(false)} color="secondary">Cancel</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom>{project?.name}</Typography>
              <Typography variant="body1" color="text.secondary">{project?.description}</Typography>
            </Box>
            {isProducer && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" onClick={() => setEditing(true)}>Edit Project</Button>
                <Button variant="contained" onClick={() => setDocumentDialogOpen(true)}>Add Document</Button>
              </Box>
            )}
          </Box>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<InfoIcon />} iconPosition="start" label={t('tabs.overview')} {...a11yProps(0)} />
            <Tab icon={<DescriptionIcon />} iconPosition="start" label={t('tabs.documents')} {...a11yProps(1)} />
            {isProducer && <Tab icon={<PeopleIcon />} iconPosition="start" label={t('tabs.team')} {...a11yProps(2)} />}
            {isProducer && <Tab icon={<MailIcon />} iconPosition="start" label={t('tabs.invitations')} {...a11yProps(3)} />}
          </Tabs>
        </Box>

        {/* Tab: Overview */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
                label={t('projectCard.documents')}
                value={documentStats.total}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<VisibilityIcon sx={{ fontSize: 32 }} />}
                label={t('documents.views')}
                value={documentStats.totalViews}
                color="info.main"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<Box component="span" sx={{ fontSize: 24 }}>$</Box>}
                label={t('status.paid')}
                value={documentStats.paid}
                color="success.main"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<Box component="span" sx={{ fontSize: 24 }}>↓</Box>}
                label={t('status.downloaded')}
                value={documentStats.downloaded}
                color="primary.main"
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>{t('overview.projectInfo')}</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('overview.createdAt')}: {project?.createdAt?.toDate?.()?.toLocaleDateString() || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('overview.owner')}: {project?.owner?.email || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('overview.participants')}: {participants.length}
            </Typography>
          </Paper>
        </TabPanel>

        {/* Tab: Documents */}
        <TabPanel value={tabValue} index={1}>
          <DataTable
            columns={documentColumns}
            data={documents}
            searchable
            searchPlaceholder={t('documents.search')}
            emptyMessage={t('documents.no_documents')}
            onRowClick={handleViewDocument}
          />

          {/* Trash Section */}
          {isProducer && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="text"
                onClick={handleToggleTrash}
                startIcon={showTrash ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: 'text.secondary' }}
              >
                {t('documents.view_trash')} ({trashedDocuments.length})
              </Button>
              <Collapse in={showTrash}>
                <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                  {loadingTrash ? (
                    <CircularProgress size={24} />
                  ) : trashedDocuments.length > 0 ? (
                    <List>
                      {trashedDocuments.map((doc, index) => (
                        <Box key={index}>
                          <ListItem sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
                            <ListItemText
                              primary={doc?.title}
                              secondary={`${t('documents.deleted_at')}: ${doc?.deletedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}`}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Tooltip title={t('documents.restore')}>
                                <IconButton size="small" color="success" onClick={() => handleRestoreClick(doc)}>
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('documents.delete_permanently')}>
                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(doc, 'hard')}>
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItem>
                          <Divider />
                        </Box>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t('documents.trash_empty')}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}
        </TabPanel>

        {/* Tab: Team (Producers only) */}
        {isProducer && (
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>{t('team.participants')}</Typography>
            <List>
              {participants.map((p, index) => (
                <Box key={index}>
                  <ListItem>
                    <ListItemText
                      primary={p.participant?.email || 'Unknown'}
                      secondary={`${t('team.role')}: ${capitalizeFirstChar(p.role)}`}
                    />
                  </ListItem>
                  <Divider />
                </Box>
              ))}
              {participants.length === 0 && (
                <ListItem>
                  <ListItemText primary={t('team.no_participants')} />
                </ListItem>
              )}
            </List>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" color="secondary" onClick={() => { setInviteRole('producer'); setInviteDialogOpen(true); }}>
                {t('team.invite_producer')}
              </Button>
              <Button variant="contained" color="secondary" onClick={() => { setInviteRole('consumer'); setInviteDialogOpen(true); }}>
                {t('team.invite_consumer')}
              </Button>
            </Box>
          </TabPanel>
        )}

        {/* Tab: Invitations (Producer only) */}
        {isProducer && (
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>{t('invitations.title')}</Typography>
            <List>
              {invitations.map((invitation, index) => (
                <Box key={index}>
                  <ListItem sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                    <ListItemText
                      primary={invitation.email}
                      secondary={`${t('team.role')}: ${capitalizeFirstChar(invitation.role)} | ${t('invitations.invited_by')}: ${invitation.invitedBy?.email || '-'}`}
                    />
                    <ListItemText
                      primary={`${t('invitations.status')}: ${invitation.accepted ? t('invitations.accepted') : t('invitations.pending')}`}
                      secondary={`${t('invitations.created_at')}: ${invitation.createdAt?.toDate?.()?.toLocaleDateString() || '-'}`}
                    />
                    {!invitation.accepted && (
                      <Button variant="outlined" color="error" onClick={() => deleteInvitation(invitation.token)}>
                        {t('invitations.delete')}
                      </Button>
                    )}
                  </ListItem>
                  <Divider />
                </Box>
              ))}
              {invitations.length === 0 && (
                <ListItem>
                  <ListItemText primary={t('invitations.no_invitations')} />
                </ListItem>
              )}
            </List>
          </TabPanel>
        )}
      </Paper>

      {/* Document Viewer Modal */}
      <DocumentViewer
        open={viewerOpen}
        onClose={() => { setViewerOpen(false); setSelectedViewDocument(null); setViewerMode('auto'); }}
        document={selectedViewDocument}
        showWatermark={!isProducer && normalizeStatus(selectedViewDocument?.status) !== 'paid'}
        viewMode={viewerMode}
        onStatusChange={getData}
        isProducer={isProducer}
      />

      {/* Watermark Configuration Dialog */}
      <WatermarkConfigDialog
        open={watermarkConfigOpen}
        onClose={() => { setWatermarkConfigOpen(false); setWatermarkConfigDoc(null); }}
        document={watermarkConfigDoc}
        onGenerate={handleWatermarkConfigGenerate}
      />

      {/* Add Document Dialog */}
      <Dialog open={documentDialogOpen} onClose={() => { if (!uploading) { setDocumentDialogOpen(false); setDocumentTitle(''); setDocumentFile(null); }}} fullWidth maxWidth="sm">
        <DialogTitle>{t('documents.add_document')}</DialogTitle>
        <DialogContent>
          {uploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress size={48} />
              <Typography sx={{ mt: 2 }}>{t('documents.uploading')}</Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label={t('documents.document_title')}
                value={documentTitle}
                onChange={(e) => setDocumentTitle(e.target.value)}
                sx={{ mt: 1, mb: 2 }}
              />
              <TextField
                fullWidth
                label={t('documents.document_description')}
                value={documentDescription}
                onChange={(e) => setDocumentDescription(e.target.value)}
                sx={{ mt: 1, mb: 2 }}
              />
              <PdfToImageUpload setValue={(e) => setDocumentFile(e)} />
            </>
          )}
          <div ref={uploadRef} style={{ display: 'none' }}>
            <IKUpload
              onError={(e) => {
                console.error('Upload error:', e);
                setUploading(false);
                setSnackbar({ open: true, message: t('documents.upload_error'), severity: 'error' });
              }}
              onSuccess={handleUploadSuccess}
              useUniqueFileName={true}
              folder={`/docuvault/${projectId}/originals`}
            />
          </div>
        </DialogContent>
        {!uploading && documentTitle !== '' && (
          <DialogActions>
            <Button onClick={() => { setDocumentDialogOpen(false); setDocumentTitle(''); setDocumentFile(null); }} color="secondary">
              {t('documents.cancel')}
            </Button>
            {documentFile && <Button onClick={handleAddDocument} variant="contained">{t('documents.add_document')}</Button>}
          </DialogActions>
        )}
      </Dialog>

      {/* Hidden IKUpload for watermarked files */}
      <div ref={watermarkUploadRef} style={{ display: 'none' }}>
        <IKUpload
          onError={handleWatermarkUploadError}
          onSuccess={handleWatermarkUploadSuccess}
          useUniqueFileName={true}
          folder={`/docuvault/${projectId}/watermarked`}
        />
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('invitations.invite')} {inviteRole === 'producer' ? t('team.producer') : t('team.consumer')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('invitations.email')}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} color="secondary">{t('documents.cancel')}</Button>
          <Button onClick={handleInvite} variant="contained">{t('invitations.invite')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedDocument(null); }}
        onConfirm={handleConfirmDelete}
        title={deleteType === 'soft' ? t('documents.confirm_soft_delete_title') : t('documents.confirm_hard_delete_title')}
        message={deleteType === 'soft'
          ? t('documents.confirm_soft_delete_message', { title: selectedDocument?.title })
          : t('documents.confirm_hard_delete_message', { title: selectedDocument?.title })
        }
        confirmText={deleteType === 'soft' ? t('documents.move_to_trash') : t('documents.delete_permanently')}
        cancelText={t('documents.cancel')}
        confirmColor={deleteType === 'soft' ? 'warning' : 'error'}
        loading={deleting}
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmDialog
        open={restoreDialogOpen}
        onClose={() => { setRestoreDialogOpen(false); setSelectedDocument(null); }}
        onConfirm={handleConfirmRestore}
        title={t('documents.confirm_restore_title')}
        message={t('documents.confirm_restore_message', { title: selectedDocument?.title })}
        confirmText={t('documents.restore')}
        cancelText={t('documents.cancel')}
        confirmColor="success"
        loading={restoring}
      />

      {/* View History Dialog */}
      <Dialog open={viewHistoryOpen} onClose={handleCloseViewHistory} fullWidth maxWidth="sm">
        <DialogTitle>{t('documents.view_history_title', { title: viewHistoryDoc?.title || '' })}</DialogTitle>
        <DialogContent>
          {loadingViewHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : viewHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {t('documents.no_view_history')}
            </Typography>
          ) : (
            <List>
              {viewHistory.map((view, index) => (
                <Box key={view.id || index}>
                  <ListItem>
                    <ListItemText
                      primary={view.userEmail || 'Unknown user'}
                      secondary={view.viewedAt?.toDate?.()?.toLocaleString() || '-'}
                    />
                  </ListItem>
                  {index < viewHistory.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewHistory}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Persistent Watermark Generation Snackbar */}
      <Snackbar open={watermarkSnackbarOpen} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" icon={<CircularProgress size={20} color="inherit" />}>
          {t('documents.generating_watermark')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
